import {
  TagPicker,
  TagPickerList,
  TagPickerInput,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  Tag,
  Avatar,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Collapsible from './Collapsible';
import { IGenericFilterTag } from '../../../utils/types/IGenericFilterTag';
import searchForUsers from '../../../utils/microsoftGraphApi/userSearch';

export interface ISelectedUser {
  text: string;
  graphId: string;
  email?: string;
}

type IUserSearchResult = {
  text?: string;
  secondaryText?: string;
  key?: string | number;
  graphId?: string;
};

export interface IChecklistImplementationProps {
  onSelectedItems?: (selectedItems: IGenericFilterTag) => void;
  initialValues?: IGenericFilterTag;
  label: string;
  dataKey: string;
}

const PeoplePickerCollapsibleChecklist = (props: IChecklistImplementationProps): JSX.Element => {
  const { onSelectedItems, initialValues, label, dataKey } = props;
  const [searchResults, setSearchResults] = useState<ISelectedUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const latestQueryRef = useRef<string>('');

  const selectedItems: ISelectedUser[] = useMemo(() => {
    const tags = initialValues?.values ?? [];
    return tags
      .map((tag) => ({
        graphId: tag.key != null ? String(tag.key) : '',
        text: tag.name ?? '',
      }))
      .filter((item) => item.graphId && item.text);
  }, [initialValues?.values]);

  const selectedOptionValues = selectedItems?.map((item) => item.graphId) ?? [];

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (e, data) => {
    if (data.value === 'no-options' || data.value === 'loading') {
      return;
    }

    const newSelectedItems = data.selectedOptions
      .map((graphId) => {
        const existing = selectedItems?.find((item) => item.graphId === graphId);
        if (existing) return existing;
        const searched = searchResults.find((item) => item.graphId === graphId);
        return searched;
      })
      .filter((item): item is ISelectedUser => item !== undefined);

    onSelectedItems({
      dataKey,
      title: label,
      values: newSelectedItems.map((item) => ({
        name: item.text,
        key: item.graphId,
      })),
    });
  };

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(async (e) => {
    const { value } = e.target;
    latestQueryRef.current = value;

    if (value && value.length > 2) {
      setIsSearching(true);
      const currentQuery = value;
      try {
        const users = (await searchForUsers(value)) as unknown as IUserSearchResult[];
        // Only update state if this is still the latest query
        if (latestQueryRef.current === currentQuery) {
          const mappedUsers: ISelectedUser[] = users
            .map((user) => {
              const graphId = (user.graphId ?? user.key ?? '').toString();
              const text = user.text || '';
              return {
                text,
                graphId,
                email: user.secondaryText || '',
              };
            })
            .filter((user) => user.graphId && user.text);
          setSearchResults(mappedUsers);
        }
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, []);

  const availableOptions = searchResults.filter(
    (option) => !selectedOptionValues.includes(option.graphId),
  );

  const getEmptyMessage = () => {
    if (searchResults.length === 0 && !isSearching) {
      return 'Type to search for users';
    }
    return 'No options available';
  };

  const renderPickerList = () => {
    if (isSearching) {
      return <TagPickerOption value="loading">Loading...</TagPickerOption>;
    }

    if (availableOptions.length > 0) {
      return availableOptions.map((option) => (
        <TagPickerOption
          secondaryContent={option.email || 'No email available'}
          media={<Avatar shape="square" aria-hidden name={option.text} color="colorful" />}
          value={option.graphId}
          key={option.graphId}
        >
          {option.text}
        </TagPickerOption>
      ));
    }

    return <TagPickerOption value="no-options">{getEmptyMessage()}</TagPickerOption>;
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Collapsible text={label}>
        <TagPicker onOptionSelect={onOptionSelect} selectedOptions={selectedOptionValues}>
          <TagPickerControl>
            <TagPickerGroup aria-label={`Selected ${label}`}>
              {selectedItems?.map((item) => (
                <Tag
                  key={item.graphId}
                  shape="rounded"
                  media={<Avatar aria-hidden name={item.text} color="colorful" />}
                  value={item.graphId}
                >
                  {item.text}
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput aria-label={`Search ${label}`} onChange={handleInputChange} />
          </TagPickerControl>

          <TagPickerList>{renderPickerList()}</TagPickerList>
        </TagPicker>
      </Collapsible>
    </FluentProvider>
  );
};

export default PeoplePickerCollapsibleChecklist;
